import * as React from 'react';

import 'react-datepicker/dist/react-datepicker.css';
import 'draft-js-static-toolbar-plugin/lib/plugin.css';
import NumberFormat from 'react-number-format';
import RichTextEditor from 'react-rte';
import {FormInput, TextField, DropdownField} from "../static/Inputs";
import {Button} from "../static/Elements";
import Icon from "../static/Icons";
import {states, provinces} from '../../utils/states';
import {postCompany, putCompany} from "../../data/jobs/jobActions";
import {companySelector} from "../../data/jobs/jobSelectors";
import createLogger from '../../utils/createLogger';
import {createDidError, createIsLoading} from "../../data/requests/requestsSelectors";
import {connect} from "react-redux";
import {Loading} from "../static/Indicators";
import PropTypes from "prop-types";

const DEBUG = true;
const debug = createLogger(DEBUG, `CompanyForm.js`);


const mapStateToProps = (state, ownProps) => {
  const isLoading = createIsLoading(CompanyForm.dependencies);
  const didError = createDidError(CompanyForm.dependencies);

  let id = ownProps.companyId;

  return {
    isAuthenticated: !!state.auth.session,
    isLoading: isLoading(state),
    didError: didError(state),
    company: id ? companySelector(state, id) : null,
  }
};

const mapDispatchToProps = {
  postCompany,
  putCompany,
};

const locationChangers = ['address', 'address2', 'city', 'state', 'country', 'zip'];
const STARTING_ID = 1000000;
let placeholderId = STARTING_ID;

class CompanyForm extends React.Component {

  static dependencies = [
    'POST_COMPANY',
    'PUT_COMPANY'
  ];

  constructor (props) {
    super (props);
    this.state = {
      canSubmit: true,
      companyData: this.loadCompanyData(),
      contactData: this.emptyContactData(),
      editorValue: RichTextEditor.createValueFromString(
        (props.company ? props.company.description : ''), 'html'),
      contacts: props.company ?
        this.objectifyContacts(props.company.contacts) : {},
    };
  };

  loadCompanyData = () => {
    const {
      company
    } = this.props;

    if (!!company) {
      return {
        status: 1,
        zip: this.props.company.zip,
        city: this.props.company.city,
        state: this.props.company.state,
        address: this.props.company.address,
        revenue: this.props.company.revenue,
        country: this.props.company.country,
        companyurl: this.props.company.companyurl,
        companyname: this.props.company.companyname,
        address2: this.props.company.address2,
        phonenumber: this.props.company.phonenumber,
        employeerange: this.props.company.employeerange,
      }
    } else {
      return {
        zip: '',
        city: '',
        status: 1,
        state: '',
        address: '',
        revenue: '',
        country: 'US',
        companyurl: '',
        companyname: '',
        phonenumber: null,
        employeerange: '1',
        address2: '',
      }
    }
  };

  emptyContactData = () => ({
    contactid: placeholderId,
    firstname: '',
    lastname: '',
    position: '',
    email: '',
    office: null,
    cell: null,
    linkedinaddress: '',
    twitteraddress: '',
    facebookaddress: '',
    googleplusaddress: '',
    isdeleted: null,
    isNew: true,
  });

  objectifyContacts = (contactsList) => {
    const contactsObj = {};
    for (let contact of contactsList) {
      contactsObj[contact['contactid']] = contact;
    }
    debug.log('Converted contacts list to object: ', contactsObj);
    return contactsObj;
  };

  listifyContacts = (contactsObj) => {
    const contactsList = [];
    for (let contactid in contactsObj) {
      contactsList.push(contactsObj[contactid])
    }
    debug.log('Converted contacts object to list: ', contactsList);
    return contactsList
  };

  onCompanyInputChange = (field) => (input) => {
    debug.log(`setting ${field} to ${input}`, input);
    this.setState({companyData: {...this.state.companyData, [field]: input}});
    if (!this.state.companyData.refreshCoordinates && locationChangers.includes(field)) {
      this.setState({companyData: {...this.state.companyData, 'refreshCoordinates': 1}})
    }
  };

  onTextEditorChange = (value) => {
    this.setState({editorValue: value});
  };

  onCompanyPhoneChange = (field) => (values) => {
    const {formattedValue} = values;
    debug.log(`setting ${field} to ${formattedValue}`);
    this.setState({companyData: {...this.state.companyData, [field]: formattedValue}})
  };

  onUpdateContact = (id) => (contact) => {
    debug.log(`Updating contact with id ${id}`);
    let contacts = this.state.contacts;
    contacts[id] = contact;
    if (!contacts[id]['isNew']) {
      contacts[id]['isChanged'] = true;
    }
    this.setState({contacts})
  };

  onAddContact = () => {
    debug.log('adding blank contact object');
    const contact = this.emptyContactData();
    placeholderId++;
    const contactid = contact.contactid;
    let contacts = {[contactid]: contact, ...this.state.contacts};
    console.log('ADDED CONTACT, here are the current contacts');
    console.log(contacts);
    this.setState({contacts});
    console.log(this.state.contacts);
  };

  onRemoveContact = (id) => () => {
    debug.log(`Removing contact with id ${id}`);
    let contacts = this.state.contacts;
    if (!contacts[id]['isNew']) {
      contacts[id]['isdeleted'] = 1;
      contacts[id]['isChanged'] = true;
    } else {
      delete contacts[id]
    }
    this.setState({contacts})
  };

  styleDescription = (description) => {
    let opening_div = `<div style='font-family: Arial, Helvetica, sans-serif; font-size: 14px;'>`;
    let closing_div = `</div>`;
    let new_description = opening_div + description + closing_div;
    debug.log(new_description);
    return new_description
  };

  onSubmit = () => {
    const {postCompany, putCompany, companyId, company, setCompany} = this.props;
    const config = {
      company: this.state.companyData,
      contacts: this.listifyContacts(this.state.contacts),
    };
    let description_html = this.state.editorValue.toString('html');
    config['company']['description'] = this.styleDescription(description_html);
    debug.log('Publishing company...', config.company);
    debug.log('...with contacts...', config.contacts);
    if (!!company) {
      console.log('calling putCompany');
      putCompany(companyId, config)
        .then(() => {
          const {returnToLead} = this.props;
          returnToLead(companyId);
        })
        .catch((error) => {
          debug.log(error);
        })
    } else {
      console.log('calling postCompany');
      postCompany(config)
        .then((response) => {
          let realId = response.data.company._id;
          setCompany(realId);
          const {returnToLead} = this.props;
          returnToLead(realId);
        })
        .catch((error) => {
          debug.log(error);
        })
    }
  };

  onCancel = () => { this.props.closeModal() };
  disableSubmit = () => { this.setState({canSubmit: false}) };
  enableSubmit = () => { this.setState({canSubmit: true}) };

  render () {

    const {
      companyData,
      editorValue,
      contacts,
      canSubmit,
    } = this.state;

    const {
      companyname,
      companyurl,
      phonenumber,
      status,
      revenue,
      employeerange,
      address,
      address2,
      country,
      state,
      city,
      zip,
    } = companyData;

    const {
      onCompanyInputChange,
      onCompanyPhoneChange,
      onTextEditorChange,
      onRemoveContact,
      onUpdateContact,
      onAddContact,
      onSubmit,
      disableSubmit,
      enableSubmit,
    } = this;

    const {
      returnToLead,
      company,
      isLoading,
    } = this.props;

    const companyNameField = {
      type: 'text',
      label: 'Company Name',
      value: companyname,
      onChange: onCompanyInputChange(`companyname`)
    };

    const companyUrlField = {
      type: 'text',
      label: 'Company Homepage',
      value: companyurl,
      onChange: onCompanyInputChange(`companyurl`)
    };

    const phoneField = {
      format: '(###) ###-####',
      mask: '_',
      onValueChange: onCompanyPhoneChange('phonenumber'),
      value: phonenumber,
    };

    const revenueField = {
      label: 'Revenue',
      type: 'text',
      value: revenue,
      onChange: onCompanyInputChange('revenue'),
    };

    const sizeField = {
      label: 'Employees',
      onChange: onCompanyInputChange('employeerange'),
      value: employeerange,
      type: 'text',
    };

    const statusField = {
      label: 'Status',
      onChange: onCompanyInputChange(`status`),
      value: status,
      defaultValue: 'Active',
      type: 'text',
    };

    const addressField = {
      type: 'text',
      label: 'Address',
      value: address,
      onChange: onCompanyInputChange('address'),
    };

    const addressLineTwoField = {
      type: 'text',
      label: 'Address Line 2',
      value: address2,
      onChange: onCompanyInputChange('address2'),
    };

    const countryField = {
      label: 'Country',
      value: country,
      onChange: onCompanyInputChange('country'),
    };

    const stateField = {
      label: 'State',
      value: state,
      onChange: onCompanyInputChange(`state`),
    };

    const cityField = {
      type: 'text',
      label: 'City',
      value: city,
      onChange: onCompanyInputChange(`city`),
    };

    const zipField = {
      type: 'number',
      label: 'Zip Code',
      value: zip,
      onChange: onCompanyInputChange(`zip`),
    };

    const toolbarConfig = {
      display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'BLOCK_TYPE_DROPDOWN', 'HISTORY_BUTTONS'],
      INLINE_STYLE_BUTTONS: [
        {label: 'Bold', style: 'BOLD', className: 'rte-toolbar'},
        {label: 'Italic', style: 'ITALIC'},
        {label: 'Underline', style: 'UNDERLINE'}
      ],
      BLOCK_TYPE_DROPDOWN: [
        {label: 'Normal', style: 'unstyled'},
        {label: 'Heading Large', style: 'header-one'},
        {label: 'Heading Medium', style: 'header-two'},
        {label: 'Heading Small', style: 'header-three'}
      ],
      BLOCK_TYPE_BUTTONS: [
        {label: 'UL', style: 'unordered-list-item'},
        {label: 'OL', style: 'ordered-list-item'}
      ]
    };

    if (isLoading) {
      return (
          <article className="details">
            <Loading/>
          </article>
      )
    }

    return (
      <FormInput className='add-company-form'>

        <div className="add-company-fields">

          <div className="full-line-field">
            <TextField {...companyNameField} />
          </div>

          <div className="full-line-field">
            <TextField {...companyUrlField} />
          </div>

          <div className="split-line-field">
            <div>
              <label>Company Phone Number</label>
              <NumberFormat {...phoneField} />
            </div>
            <TextField {...revenueField} />
          </div>

          <div className="thirds-line-field">
            <DropdownField {...sizeField}>
              <option value='1' className={'dropdown-item'}>1</option>
              <option value='2-10' className={'dropdown-item'}>2-10</option>
              <option value='11-50' className={'dropdown-item'}>11-50</option>
              <option value='51-200' className={'dropdown-item'}>51-200</option>
              <option value='201-500' className={'dropdown-item'}>201-500</option>
              <option value='501-1,000' className={'dropdown-item'}>501-1,000</option>
              <option value='1,001-5,000' className={'dropdown-item'}>1,001-5,000</option>
              <option value='5,001-10,000' className={'dropdown-item'}>5,000-10,000</option>
              <option value='10,000+' className={'dropdown-item'}>10,000+</option>
            </DropdownField>
            <DropdownField {...statusField}>
              <option value={1} className={'dropdown-item'}>Active</option>
              <option value={0} className={'dropdown-item'}>Inactive</option>
            </DropdownField>
            <DropdownField {...countryField}>
              <option value="US" className="dropdown-item">United States</option>
              <option value="CA" className="dropdown-item">Canada</option>
            </DropdownField>
          </div>

          <div className="full-line-field">
            <TextField {...addressField} />
          </div>

          <div className="full-line-field">
            <TextField {...addressLineTwoField} />
          </div>

          <div className="full-line-field">
            <TextField {...cityField}/>
          </div>

          <div className='split-line-field'>
            <DropdownField {...stateField}>
              {
                country === 'CA' ? (
                  provinces.map(province => (
                    <option value={province} key={province} className='dropdown-item'>{province}</option>
                  ))
                ) : (
                  states.map((stateName) => (
                    <option value={stateName} key={stateName} className='dropdown-item'>{stateName}</option>
                  ))
                )
              }
            </DropdownField>
            <TextField {...zipField}/>
          </div>

          <div className="full-line-field editorStyles.editor">
            <label>Company Description</label>
            <RichTextEditor value={editorValue}
                            onChange={onTextEditorChange}
                            toolbarConfig={toolbarConfig}
                            className='rte-textarea'/>
          </div>

        </div>

        <div className="company-contacts">
          <div className='company-contacts-header'>
            <h5>Contacts</h5>
            <Button primary onClick={onAddContact}>Add New Contact</Button>
          </div>
          {
            contacts ?
              (
                Object.keys(contacts).reverse().map((id) => (
                  <ContactItem contact={contacts[id]}
                               remove={onRemoveContact(id)}
                               key={id} id={id} editable={false}
                               onUpdateContact={onUpdateContact(id)}
                               disableSubmit={disableSubmit} enableSubmit={enableSubmit}/>
                ))
              ) : null
          }
        </div>
        <div className="form-footer">
          <Button key='3' danger onClick={returnToLead}>Cancel</Button>
          <Button key='4' primary onClick={onSubmit}>Submit</Button>
        </div>
      </FormInput>
    )
  }
}

class ContactItem extends React.Component {

  constructor (props) {
    super (props);
    this.state = {
      editable: props.contact['isNew'],
      contactData: this.loadContactData(),
    }
  }

  loadContactData = () => {
    return {
      contactid: this.props.contact.contactid,
      firstname: this.props.contact.firstname || '',
      lastname: this.props.contact.lastname || '',
      position: this.props.contact.position || '',
      email: this.props.contact.email || '',
      office: this.props.contact.officephone || null,
      cell: this.props.contact.phonenumber || null,
      linkedinaddress: this.props.contact.linkedinaddress || '',
      twitteraddress: this.props.contact.twitteraddress || '',
      facebookaddress: this.props.contact.facebookaddress || '',
      googleplusaddress: this.props.contact.googleplusaddress || '',
      isNew: this.props.contact.isNew || false,
      isChanged: this.props.contact.isChanged || false,
    }
  };

  componentDidMount () {
    if (this.state.editable === true) {
      this.props.disableSubmit();
    }
  }

  editMode = () => {
    this.setState({editable: true});
    this.props.disableSubmit();
  };

  updateContact = () => {
    const {onUpdateContact} = this.props;
    const contact = this.state.contactData;
    onUpdateContact(contact);
    this.setState({editable: false});
    this.props.enableSubmit();
  };

  onPhoneChange = (field) => (values) => {
    const {formattedValue} = values;
    debug.log(`setting ${field} to ${formattedValue}`);
    this.setState({contactData: {...this.state.contactData, [field]: formattedValue}})
  };

  onChange = (field) => (input) => {
    debug.log(`setting ${field} to ${input}`, input);
    this.setState({contactData: {...this.state.contactData, [field]: input}});
  };

  onCancel = () => {
    if (this.state.contactData['isNew']) {
      this.props.remove();
    } else {
      this.setState({contactData: this.loadContactData()});
      this.updateContact();
    }
    this.setState({editable: false});
    this.props.enableSubmit();
  };

  render () {
    const phoneField = {
      format: '(###) ###-####',
      mask: '_',
    };

    const {
      editMode,
      updateContact,
      onPhoneChange,
      onChange,
      onCancel,
    } = this;

    const {
      editable
    } = this.state;

    const {
      contact,
      onSubmit,
      remove,
    } = this.props;

    const {
      firstname,
      lastname,
      position,
      email,
      cell,
      office,
      linkedinaddress,
      twitteraddress,
      facebookaddress,
      googleplusaddress,
    } = this.state.contactData;

    if (contact.isdeleted) {
      return null
    }

    const staticContact = (
      <div className="contact-entry">
        <div className='contact-list-item-text'>
          <p className='no-margin'>
            {contact['position']} ({firstname} {lastname})</p>
          <a href="#" onClick={()=>window.open(linkedinaddress)}>{linkedinaddress || ''}</a>
        </div>
        <div className='contact-entry-actions'>
          <Button lowkey onClick={editMode}>
            <Icon.Edit/>
          </Button>
          <Button lowkey danger onClick={remove}>
            <Icon.Close/>
          </Button>
        </div>
      </div>
    );

    const editableContact = (
      <div className='new-contact'>
        <div className="split-line-field">
          <TextField label='First Name' type='text' value={firstname} onChange={onChange('firstname')}/>
          <TextField label='Last Name' type='text' value={lastname} onChange={onChange('lastname')}/>
        </div>
        <TextField label='Position' type='text' value={position} onChange={onChange('position')}/>
        <TextField label='Email' type='text' value={email} onChange={onChange('email')}/>
        <div className="split-line-field">
          <div>
            <label>Cell Phone</label>
            <NumberFormat {...phoneField} value={cell} onValueChange={onPhoneChange('cell')}/>
          </div>
          <div>
            <label>Office Phone</label>
            <NumberFormat {...phoneField} value={office} onValueChange={onPhoneChange('office')}/>
          </div>
        </div>
        <TextField label='LinkedIn Address' type='text' value={linkedinaddress}
                   onChange={onChange('linkedinaddress')}/>
        <TextField label='Twitter Address' type='text' value={twitteraddress}
                   onChange={onChange('twitteraddress')}/>
        <TextField label='Facebook Address' type='text' value={facebookaddress}
                   onChange={onChange('facebookaddress')}/>
        <TextField label='Google+ Address' type='text' value={googleplusaddress}
                   onChange={onChange('googleplusaddress')}/>
        <div className="space-between">
          <Button onClick={onCancel} lowkey danger>Cancel</Button>
          <Button onClick={updateContact} primary>Save</Button>
        </div>
      </div>
    );

    return (
      <React.Fragment>
        {
          editable ? editableContact : staticContact
        }
      </React.Fragment>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CompanyForm);


// WEBPACK FOOTER //
// ./src/components/AgencyPostings/CompanyForm.js